'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Remembers where a tab was scrolled when a reader took over from its content, and puts the reader
 * back there once they close it. Documents are opened from partway down an agenda that can run to
 * many screens, and returning to its top would cost the reader their place in it.
 *
 * Call the returned function immediately before opening the reader. It only records the first open,
 * so moving between documents inside the reader still returns to the original spot; deep links and
 * auto-opens never record one, since there is no position to go back to.
 *
 * @param isOpen - Whether the reader currently replaces the tab's own content.
 */
export function usePreservedScroll(isOpen: boolean) {
  const preservedScrollY = useRef<number | null>(null);

  const preserveScroll = useCallback(() => {
    if (preservedScrollY.current === null) {
      preservedScrollY.current = window.scrollY;
    }
  }, []);

  useLayoutEffect(() => {
    if (isOpen || preservedScrollY.current === null) {
      return;
    }

    window.scrollTo({ top: preservedScrollY.current, behavior: 'instant' });
    preservedScrollY.current = null;
  }, [isOpen]);

  return preserveScroll;
}
